import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Globe, ArrowUp, MapPin, Code, Server, Network, TrendingUp } from 'lucide-react';
import { translations } from '../utils/translations';

export const Footer = ({ language = 'en' }) => {
  const t = translations[language]?.footer || translations.en.footer;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: t.links.services, id: 'services' },
    { label: t.links.projects, id: 'projects' },
    { label: t.links.about, id: 'about' },
    { label: t.links.contact, id: 'contact' },
  ];

  const services = [
    { label: t.services.fullStack, icon: Code },
    { label: t.services.serverAdmin, icon: Server },
    { label: t.services.networkSupport, icon: Network },
    { label: t.services.seoMarketing, icon: TrendingUp },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="relative bg-gray-900 dark:bg-gray-950 text-white transition-colors duration-300">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 mix-blend-overlay"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <h3 className="text-2xl font-bold mb-4">
              Triarani<span className="text-blue-400">.</span>
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              {t.companyInfo.description}
            </p>
            <div className="flex items-center text-gray-400 text-sm">
              <MapPin size={16} className="mr-2" />
              <span>{t.companyInfo.location}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-4 text-white">{t.sections.quickLinks}</h4>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-gray-300 hover:text-blue-400 transition-colors duration-300 text-sm flex items-center group"
                  >
                    <div className="w-1 h-1 bg-blue-400 rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-lg mb-4 text-white">{t.sections.services}</h4>
            <ul className="space-y-3">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <li key={index} className="flex items-center text-gray-300 text-sm group">
                    <Icon size={16} className="mr-3 text-blue-400 group-hover:scale-110 transition-transform duration-300" />
                    {service.label}
                  </li>
                );
              })}
            </ul>
          </div>

           {/* Contact */}
           <div>
             <h4 className="font-bold text-lg mb-4 text-white">{t.sections.contact}</h4>
             <ul className="space-y-3">
               <li>
                 <a
                   href="mailto:edwin@triarani.com"
                   className="flex items-center gap-3 text-gray-300 hover:text-blue-400 transition-colors duration-300 text-sm group"
                 >
                   <div className="p-2 bg-blue-500/10 rounded-lg group-hover:bg-blue-500/20 transition-colors duration-300">
                     <Mail size={16} className="text-blue-400" />
                   </div>
                   {t.contactInfo.email}
                 </a>
               </li>
               <li>
                 <a
                   href="https://www.triarani.com"
                   target="_blank"
                   rel="noopener noreferrer"
                   className="flex items-center gap-3 text-gray-300 hover:text-blue-400 transition-colors duration-300 text-sm group"
                 >
                   <div className="p-2 bg-purple-500/10 rounded-lg group-hover:bg-purple-500/20 transition-colors duration-300">
                     <Globe size={16} className="text-purple-400" />
                   </div>
                   {t.contactInfo.website}
                 </a>
               </li>
             </ul>
           </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-center sm:text-left">
            <p className="text-gray-400 text-sm">
              {t.copyright} {currentYear} Triarani. {t.rightsReserved}
            </p>
            <p className="text-gray-500 text-xs mt-1">
              {t.credits}
            </p>
          </div>

          {/* Scroll to Top Button */}
          <motion.button
            onClick={scrollToTop}
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.9 }}
            className="p-3 bg-blue-600 hover:bg-blue-500 rounded-full transition-all duration-300 shadow-lg hover:shadow-blue-500/25"
            aria-label={t.scrollToTop}
          >
            <ArrowUp size={20} />
          </motion.button>
        </div>
      </div>
    </footer>
  );
};